import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  WooCoupon,
  wooGetCoupons,
  wooCreateCoupon,
  wooUpdateCoupon,
  wooDeleteCoupon,
} from "../services/woo";
import { showToast } from "../assets/util/showToast";

interface CouponsManagerProps {
  onCouponSelect?: (coupon: WooCoupon) => void;
}

type CouponFormData = {
  code: string;
  discount_type: "percent" | "fixed_cart" | "fixed_product";
  amount: string;
  description: string;
  date_expires: string;
  individual_use: boolean;
  usage_limit: string;
  usage_limit_per_user: string;
  minimum_amount: string;
  maximum_amount: string;
  free_shipping: boolean;
  exclude_sale_items: boolean;
  product_categories: number[];
  excluded_product_categories: number[];
  email_restrictions: string;
};

const CouponsManager: React.FC<CouponsManagerProps> = ({ onCouponSelect }) => {
  const [coupons, setCoupons] = useState<WooCoupon[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination and filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCoupons, setTotalCoupons] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<WooCoupon | null>(null);

  // Form state
  const [formData, setFormData] = useState<CouponFormData>({
    code: "",
    discount_type: "percent",
    amount: "",
    description: "",
    date_expires: "",
    individual_use: false,
    usage_limit: "",
    usage_limit_per_user: "",
    minimum_amount: "",
    maximum_amount: "",
    free_shipping: false,
    exclude_sale_items: false,
    product_categories: [],
    excluded_product_categories: [],
    email_restrictions: "",
  });

  // Load coupons
  const loadCoupons = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await wooGetCoupons(
        currentPage,
        20,
        searchTerm,
        statusFilter
      );
      setCoupons(result.coupons);
      setTotalPages(result.totalPages);
      setTotalCoupons(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load coupons");
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter]);

  // Load coupons on mount and when dependencies change
  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  // Filter coupons by type
  const filteredCoupons = useMemo(() => {
    if (!typeFilter) return coupons;
    return coupons.filter((coupon) => coupon.discount_type === typeFilter);
  }, [coupons, typeFilter]);

  // Reset form data
  const resetForm = () => {
    setFormData({
      code: "",
      discount_type: "percent",
      amount: "",
      description: "",
      date_expires: "",
      individual_use: false,
      usage_limit: "",
      usage_limit_per_user: "",
      minimum_amount: "",
      maximum_amount: "",
      free_shipping: false,
      exclude_sale_items: false,
      product_categories: [],
      excluded_product_categories: [],
      email_restrictions: "",
    });
  };

  // Handle create coupon
  const handleCreate = async () => {
    if (!formData.code.trim() || !formData.amount.trim()) {
      showToast("Please fill in required fields", "error");
      return;
    }

    setLoading(true);
    try {
      const couponData: any = {
        code: formData.code.trim().toUpperCase(),
        discount_type: formData.discount_type,
        amount: formData.amount,
        description: formData.description,
        individual_use: formData.individual_use,
        free_shipping: formData.free_shipping,
        exclude_sale_items: formData.exclude_sale_items,
      };

      // Optional fields
      if (formData.date_expires)
        couponData.date_expires = formData.date_expires;
      if (formData.usage_limit)
        couponData.usage_limit = parseInt(formData.usage_limit);
      if (formData.usage_limit_per_user)
        couponData.usage_limit_per_user = parseInt(
          formData.usage_limit_per_user
        );
      if (formData.minimum_amount)
        couponData.minimum_amount = formData.minimum_amount;
      if (formData.maximum_amount)
        couponData.maximum_amount = formData.maximum_amount;
      if (formData.product_categories.length > 0)
        couponData.product_categories = formData.product_categories;
      if (formData.excluded_product_categories.length > 0)
        couponData.excluded_product_categories =
          formData.excluded_product_categories;
      if (formData.email_restrictions) {
        couponData.email_restrictions = formData.email_restrictions
          .split(",")
          .map((email) => email.trim())
          .filter((email) => email);
      }

      await wooCreateCoupon(couponData);
      showToast("Coupon created successfully!", "success");
      setShowCreateModal(false);
      resetForm();
      loadCoupons();
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "Failed to create coupon",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle edit coupon
  const handleEdit = async () => {
    if (!selectedCoupon) return;

    setLoading(true);
    try {
      const couponData: any = {
        code: formData.code.trim().toUpperCase(),
        discount_type: formData.discount_type,
        amount: formData.amount,
        description: formData.description,
        individual_use: formData.individual_use,
        free_shipping: formData.free_shipping,
        exclude_sale_items: formData.exclude_sale_items,
      };

      // Optional fields
      if (formData.date_expires)
        couponData.date_expires = formData.date_expires;
      if (formData.usage_limit)
        couponData.usage_limit = parseInt(formData.usage_limit);
      if (formData.usage_limit_per_user)
        couponData.usage_limit_per_user = parseInt(
          formData.usage_limit_per_user
        );
      if (formData.minimum_amount)
        couponData.minimum_amount = formData.minimum_amount;
      if (formData.maximum_amount)
        couponData.maximum_amount = formData.maximum_amount;
      if (formData.product_categories.length > 0)
        couponData.product_categories = formData.product_categories;
      if (formData.excluded_product_categories.length > 0)
        couponData.excluded_product_categories =
          formData.excluded_product_categories;
      if (formData.email_restrictions) {
        couponData.email_restrictions = formData.email_restrictions
          .split(",")
          .map((email) => email.trim())
          .filter((email) => email);
      }

      await wooUpdateCoupon(selectedCoupon.id, couponData);
      showToast("Coupon updated successfully!", "success");
      setShowEditModal(false);
      resetForm();
      setSelectedCoupon(null);
      loadCoupons();
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "Failed to update coupon",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle delete coupon
  const handleDelete = async () => {
    if (!selectedCoupon) return;

    setLoading(true);
    try {
      await wooDeleteCoupon(selectedCoupon.id);
      showToast("Coupon deleted successfully!", "success");
      setShowDeleteModal(false);
      setSelectedCoupon(null);
      loadCoupons();
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "Failed to delete coupon",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Open edit modal
  const openEditModal = (coupon: WooCoupon) => {
    setSelectedCoupon(coupon);
    setFormData({
      code: coupon.code,
      discount_type: coupon.discount_type,
      amount: coupon.amount,
      description: coupon.description || "",
      date_expires: coupon.date_expires
        ? coupon.date_expires.split("T")[0]
        : "",
      individual_use: coupon.individual_use,
      usage_limit: coupon.usage_limit?.toString() || "",
      usage_limit_per_user: coupon.usage_limit_per_user?.toString() || "",
      minimum_amount: coupon.minimum_amount || "",
      maximum_amount: coupon.maximum_amount || "",
      free_shipping: coupon.free_shipping,
      exclude_sale_items: coupon.exclude_sale_items,
      product_categories: coupon.product_categories || [],
      excluded_product_categories: coupon.excluded_product_categories || [],
      email_restrictions: coupon.email_restrictions?.join(", ") || "",
    });
    setShowEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (coupon: WooCoupon) => {
    setSelectedCoupon(coupon);
    setShowDeleteModal(true);
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Get discount type badge
  const getDiscountTypeBadge = (type: string) => {
    const badges = {
      percent: { class: "bg-success", text: "Percentage" },
      fixed_cart: { class: "bg-primary", text: "Fixed Cart" },
      fixed_product: { class: "bg-info", text: "Fixed Product" },
    };
    const badge = badges[type as keyof typeof badges] || {
      class: "bg-secondary",
      text: type,
    };
    return <span className={`badge ${badge.class}`}>{badge.text}</span>;
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    const badges = {
      publish: { class: "bg-success", text: "Active" },
      draft: { class: "bg-warning", text: "Draft" },
      pending: { class: "bg-info", text: "Pending" },
      private: { class: "bg-secondary", text: "Private" },
    };
    const badge = badges[status as keyof typeof badges] || {
      class: "bg-secondary",
      text: status,
    };
    return <span className={`badge ${badge.class}`}>{badge.text}</span>;
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Coupons Manager</h2>
          <p className="text-muted mb-0">
            Manage your WooCommerce discount coupons
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
          disabled={loading}
        >
          <i className="fas fa-plus me-2"></i>
          Create Coupon
        </button>
      </div>

      {error && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Search Coupons</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by coupon code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="publish">Active</option>
                <option value="draft">Draft</option>
                <option value="pending">Pending</option>
                <option value="private">Private</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Discount Type</label>
              <select
                className="form-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="">All Types</option>
                <option value="percent">Percentage</option>
                <option value="fixed_cart">Fixed Cart</option>
                <option value="fixed_product">Fixed Product</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">&nbsp;</label>
              <button
                className="btn btn-outline-secondary d-block w-100"
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("");
                  setTypeFilter("");
                  setCurrentPage(1);
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results Info */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <span className="text-muted">
            Showing {filteredCoupons.length} of {totalCoupons} coupons
          </span>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={loadCoupons}
            disabled={loading}
          >
            <i className="fas fa-sync me-1"></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Coupons Table */}
      {loading && filteredCoupons.length === 0 ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : filteredCoupons.length === 0 ? (
        <div className="text-center py-5">
          <i className="fas fa-ticket-alt fa-3x text-muted mb-3"></i>
          <h5>No coupons found</h5>
          <p className="text-muted">
            {searchTerm || statusFilter || typeFilter
              ? "Try adjusting your filters"
              : "Create your first coupon to get started"}
          </p>
        </div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Usage</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCoupons.map((coupon) => (
                  <tr key={coupon.id}>
                    <td>
                      <div>
                        <strong className="text-primary">{coupon.code}</strong>
                        {coupon.description && (
                          <div className="small text-muted">
                            {coupon.description.length > 50
                              ? `${coupon.description.substring(0, 50)}...`
                              : coupon.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>{getDiscountTypeBadge(coupon.discount_type)}</td>
                    <td>
                      <strong>
                        {coupon.discount_type === "percent"
                          ? `${coupon.amount}%`
                          : `$${coupon.amount}`}
                      </strong>
                    </td>
                    <td>
                      <div className="small">
                        <div>Used: {coupon.usage_count}</div>
                        {coupon.usage_limit && (
                          <div className="text-muted">
                            Limit: {coupon.usage_limit}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      {coupon.date_expires ? (
                        <div className="small">
                          {formatDate(coupon.date_expires)}
                        </div>
                      ) : (
                        <span className="text-muted">No expiry</span>
                      )}
                    </td>
                    <td>{getStatusBadge(coupon.status)}</td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        {onCouponSelect && (
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => onCouponSelect(coupon)}
                            title="Select Coupon"
                          >
                            <i className="fas fa-check"></i>
                          </button>
                        )}
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => openEditModal(coupon)}
                          title="Edit Coupon"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => openDeleteModal(coupon)}
                          title="Delete Coupon"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <nav className="mt-4">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
            </li>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <li
                  key={pageNum}
                  className={`page-item ${
                    currentPage === pageNum ? "active" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                </li>
              );
            })}

            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {showCreateModal ? "Create New Coupon" : "Edit Coupon"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    resetForm();
                    setSelectedCoupon(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <form>
                  {/* Basic Information */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Coupon Code *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.code}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            code: e.target.value.toUpperCase(),
                          })
                        }
                        placeholder="SAVE20"
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Discount Type *</label>
                      <select
                        className="form-select"
                        value={formData.discount_type}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            discount_type: e.target.value as any,
                          })
                        }
                      >
                        <option value="percent">Percentage discount</option>
                        <option value="fixed_cart">Fixed cart discount</option>
                        <option value="fixed_product">
                          Fixed product discount
                        </option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">
                        Amount *{" "}
                        {formData.discount_type === "percent" ? "(%)" : "($)"}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control"
                        value={formData.amount}
                        onChange={(e) =>
                          setFormData({ ...formData, amount: e.target.value })
                        }
                        placeholder={
                          formData.discount_type === "percent" ? "20" : "10.00"
                        }
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      placeholder="Optional description for this coupon"
                    />
                  </div>

                  {/* Usage Restrictions */}
                  <h6 className="border-bottom pb-2 mb-3">
                    Usage Restrictions
                  </h6>

                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label">Expiry Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.date_expires}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            date_expires: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Usage Limit</label>
                      <input
                        type="number"
                        min="0"
                        className="form-control"
                        value={formData.usage_limit}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            usage_limit: e.target.value,
                          })
                        }
                        placeholder="Unlimited"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Usage Limit Per User</label>
                      <input
                        type="number"
                        min="0"
                        className="form-control"
                        value={formData.usage_limit_per_user}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            usage_limit_per_user: e.target.value,
                          })
                        }
                        placeholder="Unlimited"
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Minimum Amount ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control"
                        value={formData.minimum_amount}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            minimum_amount: e.target.value,
                          })
                        }
                        placeholder="No minimum"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Maximum Amount ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control"
                        value={formData.maximum_amount}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            maximum_amount: e.target.value,
                          })
                        }
                        placeholder="No maximum"
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Email Restrictions</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.email_restrictions}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          email_restrictions: e.target.value,
                        })
                      }
                      placeholder="email1@example.com, email2@example.com (comma separated)"
                    />
                  </div>

                  {/* Options */}
                  <h6 className="border-bottom pb-2 mb-3">Options</h6>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="individual_use"
                          checked={formData.individual_use}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              individual_use: e.target.checked,
                            })
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor="individual_use"
                        >
                          Individual use only
                        </label>
                      </div>
                      <div className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="free_shipping"
                          checked={formData.free_shipping}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              free_shipping: e.target.checked,
                            })
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor="free_shipping"
                        >
                          Allow free shipping
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="exclude_sale_items"
                          checked={formData.exclude_sale_items}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              exclude_sale_items: e.target.checked,
                            })
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor="exclude_sale_items"
                        >
                          Exclude sale items
                        </label>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    resetForm();
                    setSelectedCoupon(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={showCreateModal ? handleCreate : handleEdit}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      {showCreateModal ? "Creating..." : "Updating..."}
                    </>
                  ) : showCreateModal ? (
                    "Create Coupon"
                  ) : (
                    "Update Coupon"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedCoupon && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Coupon</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedCoupon(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete the coupon{" "}
                  <strong>"{selectedCoupon.code}"</strong>?
                </p>
                <div className="alert alert-warning">
                  <small>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    This action cannot be undone. The coupon will be permanently
                    deleted.
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedCoupon(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Deleting...
                    </>
                  ) : (
                    "Delete Coupon"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponsManager;
